/**
 * ダウンロード済みの音楽をIndexedDBだけで扱うためのヘルパー
 *
 * ダウンロードは全ドメイン共通の offlineEpisodes テーブルに保存され、
 * 音楽の付随情報（アーティスト・アルバム・再生時間）も保存時に一緒に持たせている。
 * そのためダウンロード済み一覧はサーバーへ問い合わせずに組み立てられる。
 */

import { useMemo } from 'react'

import type { MusicAlbumDetailResponse, MusicAlbumItem, MusicArtistItem, QueueTrackItem } from '@/api/type'
import type { OfflineEpisode, OfflineMusicMeta } from '@/lib/db'
import type { MusicTrack } from '@/store/useMusicStore'

import { MUSIC_DOMAIN } from './constants'
import { useOfflineEpisodes } from './use-offline-ids'

/** 再生中トラックの情報を、ダウンロード時に保存するメタデータへ変換する */
export const toOfflineMusicMeta = (track: MusicTrack): OfflineMusicMeta => ({
  albumTitle: track.album ?? '',
  artist: track.artist,
  artistTagId: track.artistTagId,
  duration: track.duration,
})

/** 保存済みデータはサムネイル URL を持たないため、作品 ID から組み立てる */
const toThumbnailPath = (productId: number): string => `/p/${productId}/thumbnail`

/** 保存済みエピソードを一覧表示用のトラックへ変換する（古い保存データはメタデータを持たない） */
const toQueueTrackItem = (episode: OfflineEpisode): QueueTrackItem => ({
  album_title: episode.music?.albumTitle ?? '',
  artist: episode.music?.artist ?? null,
  artist_tag_id: episode.music?.artistTagId ?? null,
  duration: episode.music?.duration ?? 0,
  episode_id: episode.episodeId,
  product_id: episode.productId,
  thumbnail: toThumbnailPath(episode.productId),
  title: episode.title,
  lyrics_first_line: null,
  lyrics_timing: null,
  release_date: null,
})

/** ダウンロード済みの曲一覧を返す（未取得のうちは null） */
export const useOfflineMusicTracks = (): QueueTrackItem[] | null => {
  const episodes = useOfflineEpisodes()

  return useMemo(() => {
    if (episodes === null) return null
    return episodes.filter((e) => e.domain === MUSIC_DOMAIN).map(toQueueTrackItem)
  }, [episodes])
}

/** ダウンロード済みの曲からアーティスト一覧を組み立てる */
export const buildOfflineArtists = (tracks: QueueTrackItem[]): MusicArtistItem[] => {
  const artists = new Map<number, { name: string; productIds: Set<number>; trackCount: number }>()

  for (const track of tracks) {
    if (track.artist_tag_id === null) continue
    const entry = artists.get(track.artist_tag_id)
    if (!entry) {
      artists.set(track.artist_tag_id, {
        name: track.artist ?? '',
        productIds: new Set([track.product_id]),
        trackCount: 1,
      })
      continue
    }
    entry.productIds.add(track.product_id)
    entry.trackCount += 1
  }

  return [...artists.entries()].map(([tagId, entry]) => ({
    album_count: entry.productIds.size,
    name: entry.name,
    // アーティスト自体の画像は持たないため、代表アルバム（最小ID）のアートワークを使う
    thumbnail: toThumbnailPath(Math.min(...entry.productIds)),
    tag_id: tagId,
    track_count: entry.trackCount,
  }))
}

/**
 * ダウンロード済みの曲から、指定アーティストのアルバム一覧を組み立てる
 *
 * リリース日は保存していないため null になる（並び替えはアルバム名で行う）。
 */
export const buildOfflineAlbums = (tracks: QueueTrackItem[], tagId: number): MusicAlbumItem[] => {
  const albums = new Map<number, { title: string; trackCount: number }>()

  for (const track of tracks) {
    if (track.artist_tag_id !== tagId) continue
    const entry = albums.get(track.product_id)
    if (!entry) {
      albums.set(track.product_id, { title: track.album_title, trackCount: 1 })
      continue
    }
    entry.trackCount += 1
  }

  return [...albums.entries()].map(([productId, entry]) => ({
    product_id: productId,
    released_at: null,
    thumbnail: toThumbnailPath(productId),
    title: entry.title,
    track_count: entry.trackCount,
  }))
}

/** ダウンロード済みの曲から、指定アーティストの名前を引く */
export const findOfflineArtistName = (tracks: QueueTrackItem[], tagId: number): string =>
  tracks.find((t) => t.artist_tag_id === tagId)?.artist ?? ''

/**
 * 保存済みエピソードからアルバム詳細を組み立てる（サーバーに繋がらないときのフォールバック）
 *
 * ジャンル・リリース日は保存していないため null になり、トラック番号は保存順の連番で代用する。
 */
export const buildOfflineAlbumDetail = (
  episodes: OfflineEpisode[],
  productId: number,
): MusicAlbumDetailResponse | null => {
  const tracks = episodes
    .filter((e) => e.domain === MUSIC_DOMAIN && e.productId === productId)
    .map(toQueueTrackItem)
    .sort((a, b) => a.episode_id - b.episode_id)

  const first = tracks.at(0)
  if (!first) return null

  return {
    artist_name: first.artist,
    artist_tag_id: first.artist_tag_id,
    genre: null,
    product_id: productId,
    released_at: null,
    thumbnail: toThumbnailPath(productId),
    title: first.album_title,
    total_duration: tracks.reduce((sum, t) => sum + t.duration, 0),
    tracks: tracks.map((t, i) => ({
      duration: t.duration,
      episode_id: t.episode_id,
      index: i + 1,
      // 歌詞の有無はオフラインでは保存していない
      lyrics_timing: null,
      title: t.title,
    })),
  }
}
